import { inject, Injectable } from '@angular/core';

import { Subject } from 'rxjs';
import { debounceTime, groupBy, mergeMap } from 'rxjs/operators';

import type { CurrentUser } from '@core/account-types';
import {
  ADMIN,
  COMPANY_ID,
  promiseCompatProxy,
  IndexedDbService,
  LocalStorageService,
} from '@core/common-legacy';
import type { MailboxFormMessageData, MailboxNewMessageAction } from '@core/mailbox-types';
import { injectIsOneDomain } from '@core/tellent-util';

type MailboxCacheData = { action: MailboxNewMessageAction; formValues: any };

@Injectable({ providedIn: 'root' })
export class MailboxCache {
  #isOneDomain = injectIsOneDomain();

  public localStorageService = inject(LocalStorageService);
  public indexedDbService = inject(IndexedDbService);
  public companyId = inject<number>(COMPANY_ID);
  public admin = inject<CurrentUser>(ADMIN);

  private formChanges$ = new Subject<{
    key: string;
    action: MailboxNewMessageAction | null | undefined;
    formValues: MailboxFormMessageData;
  }>();

  constructor() {
    this.handleFutureFormChanges();
  }

  public publish({
    key,
    action,
    formValues,
  }: {
    key: string;
    action: MailboxNewMessageAction | null | undefined;
    formValues: MailboxFormMessageData;
  }): void {
    // Don't update cache if browser tab is not active.
    // This fixes case where user has multiple recruitee tabs opened. In first tab there is a form
    // with a draft message. When in another tab said draft is sent, first one will keep putting
    // it back into local storage, and propagating it to other tabs.
    if (!document.hasFocus()) return;
    this.formChanges$.next({ key, action, formValues });
  }

  public get(key: string) {
    return this.#isOneDomain
      ? this.indexedDbService.get<MailboxCacheData>(key, null)
      : promiseCompatProxy(Promise.resolve(this.localStorageService.get(key)));
  }

  public async remove(key: string): Promise<void> {
    return this.#isOneDomain
      ? this.indexedDbService.remove(key)
      : promiseCompatProxy(Promise.resolve(this.localStorageService.remove(key)));
  }

  public key(kind: string, id: number | null = null): string {
    return ['mailbox', kind, id, this.companyId, this.admin.id].filter(Boolean).join('.');
  }

  private handleFutureFormChanges(): void {
    this.formChanges$
      .asObservable()
      .pipe(
        groupBy(data => data['key']),
        mergeMap(obs => obs.pipe(debounceTime(50))),
      )
      .subscribe(data => {
        this.#isOneDomain
          ? this.indexedDbService.set(data['key'], {
              action: data['action'],
              formValues: data['formValues'],
            })
          : this.localStorageService.set(data['key'], {
              action: data['action'],
              formValues: data['formValues'],
            });
      });
  }
}
